import React, { useState } from 'react';

interface LauraChatProps {
  message: string;
  showAvatar?: boolean;
  showInput?: boolean;
  onSendMessage?: (message: string) => void;
  inputPlaceholder?: string;
}

const LauraChat: React.FC<LauraChatProps> = ({
  message,
  showAvatar = true,
  showInput = false,
  onSendMessage,
  inputPlaceholder = "Ask Laura a question...",
}) => {
  const [inputValue, setInputValue] = useState('');
  const [imageError, setImageError] = useState(false);

  const handleSend = () => {
    if (inputValue.trim() && onSendMessage) {
      onSendMessage(inputValue.trim());
      setInputValue('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.chatArea}>
        {showAvatar && (
          <div style={styles.avatar}>
            {!imageError ? (
              <img
                src="/images/laura.png"
                alt="Laura"
                style={styles.avatarImage}
                onError={() => setImageError(true)}
              />
            ) : (
              <span style={styles.avatarEmoji}>👩‍🌾</span>
            )}
          </div>
        )}
        <div style={styles.bubble}>
          <div style={styles.name}>Laura</div>
          <p style={styles.message}>{message}</p>
        </div>
      </div>

      {showInput && (
        <div style={styles.inputArea}>
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={inputPlaceholder}
            style={styles.input}
          />
          <button
            onClick={handleSend}
            style={styles.sendButton}
            disabled={!inputValue.trim()}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    backgroundColor: '#fff',
    borderBottom: '1px solid #e0e0e0',
  },
  chatArea: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    padding: '1rem 1.5rem',
  },
  avatar: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: '#e8f5e9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    border: '2px solid #508590',
    overflow: 'hidden',
  },
  avatarImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'top',
  },
  avatarEmoji: {
    fontSize: '1.75rem',
  },
  bubble: {
    flex: 1,
    backgroundColor: '#f0f7f8',
    padding: '0.875rem 1rem',
    borderRadius: '0 12px 12px 12px',
    maxWidth: '500px',
  },
  name: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#508590',
    marginBottom: '0.25rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  message: {
    margin: 0,
    color: '#333',
    fontSize: '1rem',
    lineHeight: '1.5',
    fontFamily: "'Open Sans', sans-serif",
  },
  inputArea: {
    display: 'flex',
    gap: '0.5rem',
    padding: '0 1.5rem 1rem',
    paddingLeft: 'calc(1.5rem + 48px + 0.75rem)', // Align with message bubble
  },
  input: {
    flex: 1,
    padding: '0.75rem 1rem',
    border: '2px solid #e0e0e0',
    borderRadius: '24px',
    fontSize: '0.9375rem',
    outline: 'none',
    fontFamily: "'Open Sans', sans-serif",
  },
  sendButton: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    border: 'none',
    backgroundColor: '#508590',
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
};

export default LauraChat;
